import axios from "axios";

export const baseURL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/";

const api = axios.create({
  baseURL,
});

let isTerminatingSession = false;
let sessionTerminatedTimeout = null;

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const forceLogout = () => {
  if (sessionTerminatedTimeout) {
    clearTimeout(sessionTerminatedTimeout);
    sessionTerminatedTimeout = null;
  }
  isTerminatingSession = false;
  isRefreshing = false;
  processQueue(new Error('Session terminated/logged out'), null);
  try {
    const { closeSessionTerminatedPopup } = require('../components/common/ErrorAlert/ErrorAlert');
    closeSessionTerminatedPopup();
  } catch (e) {}

  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('bearer');

  window.dispatchEvent(new Event("authLogout")); 

  if (window.location.pathname !== '/login') {
    window.location.href = '/login'; 
  }
};

const logout = () => {
  // If a session-terminated popup is currently active, do NOT let other requests
  // dismiss the popup and abruptly redirect to login
  if (isTerminatingSession) {
    return;
  }
  forceLogout();
};


// Login function
export const login = async (email, password) => {
  const loginPayload = {
    email: email?.trim() || '',
    password: password || '',
    platform: 'web',
  };

  try {
    const response = await api.post('accounts/login/organizational/', loginPayload);
    const data = response.data;

    if (data.mfa_required) {
      return {
        mfa_required: true,
        mfa_type: data.mfa_type,
        mfa_token: data.mfa_token,
        masked_email: data.masked_email,
        resend_cooldown_seconds: data.resend_cooldown_seconds || 60,
      };
    }

    if (data.is_deactivated) {
      return {
        success: false,
        is_deactivated: true,
        deactivation_date: data.deactivation_date,
        email: data.email,
        message: data.message || "Account is deactivated"
      };
    }

    if (data.access && data.refresh) {
      // Store tokens in localStorage
      try {
        localStorage.setItem('accessToken', data.access);
        localStorage.setItem('refreshToken', data.refresh);
        localStorage.setItem('bearer', `Bearer ${data.access}`);
        
        const storedAccess = localStorage.getItem('accessToken');
        const storedRefresh = localStorage.getItem('refreshToken');
        
        if (!storedAccess || !storedRefresh) {
          throw new Error('Failed to store tokens');
        }
        
        return { 
          success: true, 
          message: "Login successful",
          tokens: { access: data.access, refresh: data.refresh }
        };
      } catch (storageError) {
        console.error('Storage error:', storageError);
        return { success: false, message: "Failed to store authentication tokens" };
      }
    }
    return { success: false, message: "Authentication failed: Missing tokens" };
  } catch (error) {
    console.error('API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.response?.data?.detail || 'Network error occurred' 
    };
  }
};

// Verify Email MFA OTP
export const verifyEmailMfa = async (mfa_token, otp, platform = 'web') => {
  try {
    const response = await api.post('accounts/auth/mfa/email-verify/', {
      mfa_token,
      otp,
      platform,
    });
    return response.data;
  } catch (error) {
    return {
      success: false,
      detail:
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Verification failed. Please try again.',
      session_expired: error.response?.data?.session_expired || false,
      attempts_remaining: error.response?.data?.attempts_remaining,
    };
  }
};

// Resend Email MFA OTP
export const resendEmailMfa = async (mfa_token) => {
  try {
    const response = await api.post('accounts/auth/mfa/email-resend/', {
      mfa_token,
    });
    return response.data;
  } catch (error) {
    return {
      success: false,
      detail:
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to resend verification code.',
      resend_cooldown_seconds: error.response?.data?.resend_cooldown_seconds,
    };
  }
};

// services/api.js
export const otpLogin = async (email, otp) => {
  try {
    const response = await api.post('accounts/auth/otp-login/', { email, otp, platform: 'web' });
    const data = response.data;

    if (data.is_deactivated) {
      return {
        success: false,
        is_deactivated: true,
        deactivation_date: data.deactivation_date,
        email: data.email,
        message: data.message || "Account is deactivated"
      };
    }

    if (data.access && data.refresh) {
      localStorage.setItem('accessToken', data.access);
      localStorage.setItem('refreshToken', data.refresh);
      localStorage.setItem('bearer', `Bearer ${data.access}`);
      return {
        success: true,
        tokens: { access: data.access, refresh: data.refresh }
      };
    } else {
      return { success: false, message: "Missing tokens" };
    }
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.detail || 'OTP login failed'
    };
  }
};



// Send OTP
export const sendOtp = async (email, action = 'registration') => {
  try {
    const response = await api.post('accounts/send-otp/', { email, action });
    return { success: true, message: response.data.message };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || "Failed to send OTP"
    };
  }
};


// Verify OTP
export const verifyOtp = async (email, otp) => {
  try {
    const response = await api.post('accounts/verify-otp/', { email, otp });
    return {
      success: true,
      message: "OTP verified successfully",
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || "OTP verification failed",
      data: error.response?.data
    };
  }
};



// Add a request interceptor to include the access token
// api.interceptors.request.use(
//   (config) => {
//     const token = localStorage.getItem('accessToken');
//     if (token) {
//       config.headers.Authorization = `Bearer ${token}`;
//     }
//     return config;
//   },
//   (error) => {
//     return Promise.reject(error);
//   }
// );

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Accept-Language'] = localStorage.getItem('i18nextLng') || 'en'; // ← add this
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.data?.error_code === 'FINANCIAL_YEAR_LOCKED') {
      try {
        const { showAlert } = require('../components/common/ErrorAlert/ErrorAlert');
        showAlert(error.response.data.message || 'Financial Year is locked');
      } catch (e) {
        console.error("FY Lock Error Alert:", error.response?.data?.message);
      }
    }

    if (originalRequest?.url?.includes('login')) {
      return Promise.reject(error);
    }

    const resData = error.response?.data;
    const detailMsg = typeof resData?.detail === 'string' ? resData.detail.toLowerCase() : '';
    const messageMsg = typeof resData?.message === 'string' ? resData.message.toLowerCase() : '';
    const errorMsg = typeof resData?.error === 'string' ? resData.error.toLowerCase() : '';
    const codeVal = typeof resData?.code === 'string' ? resData.code.toLowerCase() : '';
    const errorCodeVal = typeof resData?.error_code === 'string' ? resData.error_code.toLowerCase() : '';

    const isDeletedOrInactive =
      codeVal === 'user_deleted' ||
      resData?.is_deleted === true ||
      detailMsg.includes('deleted') ||
      detailMsg.includes('deactivated');

    if (isDeletedOrInactive) {
      if (!isTerminatingSession) {
        forceLogout();
      }
      return Promise.reject(error);
    }

    const isSessionTerminated =
      codeVal === 'session_terminated' ||
      codeVal === 'session_expired' ||
      errorCodeVal === 'session_terminated' ||
      errorCodeVal === 'session_expired' ||
      resData?.is_session_expired === true ||
      resData?.session_terminated === true ||
      detailMsg.includes('another device') ||
      detailMsg.includes('different session') ||
      detailMsg.includes('session_terminated') ||
      detailMsg.includes('session terminated') ||
      detailMsg.includes('logged out from another') ||
      detailMsg.includes('logged in on another') ||
      detailMsg.includes('logged in from another') ||
      messageMsg.includes('another device') ||
      messageMsg.includes('different session') ||
      messageMsg.includes('session_terminated') ||
      messageMsg.includes('session terminated') ||
      errorMsg.includes('session_terminated') ||
      errorMsg.includes('another device');

    if (isSessionTerminated) {
      if (!isTerminatingSession) {
        isTerminatingSession = true;
        // Clean tokens so any further background requests don't keep firing with an invalid session
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('bearer');

        try {
          const { showSessionTerminatedPopup } = require('../components/common/ErrorAlert/ErrorAlert');
          showSessionTerminatedPopup({
            title: "Logged Out",
            message:
              resData?.detail &&
              !resData.detail.toLowerCase().includes("redirect")
                ? resData.detail
                : resData?.message &&
                  !resData.message.toLowerCase().includes("redirect")
                ? resData.message
                : "You have been logged out because your account was logged in on another device.",
            buttonText: "Login Now",
            onConfirm: () => {
              forceLogout();
            },
          });
        } catch (e) {
          console.error("Session terminated popup error:", e);
        }
      }
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401) {
      // If session termination popup is already open, ignore further 401s from concurrent requests
      if (isTerminatingSession) {
        return Promise.reject(error);
      }

      // Do not try to refresh if the failing request was the refresh endpoint itself
      if (originalRequest?.url?.includes('accounts/token/refresh/') || originalRequest?.url?.includes('token/refresh')) {
        return Promise.reject(error);
      }

      if (originalRequest._retry) {
        logout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Enqueue concurrent requests while a refresh is in flight
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          processQueue(new Error("No refresh token available"), null);
          logout();
          return Promise.reject(error);
        }

        const response = await axios.post(`${baseURL}accounts/token/refresh/`, { refresh: refreshToken });
        if (response.status === 200 && response.data.access) {
          const { access, refresh } = response.data;
          localStorage.setItem('accessToken', access);
          localStorage.setItem('bearer', `Bearer ${access}`);
          if (refresh) {
            localStorage.setItem('refreshToken', refresh);
          }
          api.defaults.headers.common['Authorization'] = `Bearer ${access}`;
          originalRequest.headers.Authorization = `Bearer ${access}`;

          processQueue(null, access);
          return api(originalRequest);
        } else {
          processQueue(new Error("Refresh response missing access token"), null);
          logout();
          return Promise.reject(error);
        }
      } catch (err) {
        processQueue(err, null);
        console.error("Error refreshing token:", err);
        const refreshRes = err.response?.data;
        const refreshDetail = typeof refreshRes?.detail === 'string' ? refreshRes.detail.toLowerCase() : '';
        const refreshCode = typeof refreshRes?.code === 'string' ? refreshRes.code.toLowerCase() : '';
        if (
          refreshCode === 'session_terminated' ||
          refreshCode === 'session_expired' ||
          refreshRes?.is_session_expired === true ||
          refreshRes?.session_terminated === true ||
          refreshDetail.includes('another device') ||
          refreshDetail.includes('session terminated')
        ) {
          if (!isTerminatingSession) {
            isTerminatingSession = true;
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('bearer');
            try {
              const { showSessionTerminatedPopup } = require('../components/common/ErrorAlert/ErrorAlert');
              showSessionTerminatedPopup({
                title: "Logged Out",
                message: "You have been logged out because your account was logged in on another device.",
                buttonText: "Login Now",
                onConfirm: () => {
                  forceLogout();
                },
              });
            } catch (e) {
              console.error("Session terminated popup error:", e);
            }
          }
        } else {
          logout();
        }
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    // ── 403: subscription expired (set by SubscriptionGuardMiddleware) ────
    if (error.response?.status === 403 && error.response?.data?.subscription_expired === true) {
      const data = error.response.data;

      // Import Zustand store outside React — works because Zustand stores
      // are module singletons, not React context
      const { setPlanExpiredPopup } = (await import('../store/Useplanexpiredpopupstore')).default.getState();

      setPlanExpiredPopup({
        showPopup: true,
        isFreePlan: data.plan_type === 'free',
        extensionRequested: data.extension_requested ?? false,
      });

      // Resolve silently — the popup handles the UX, no need to throw
      return Promise.resolve({ data: { success: false, subscription_expired: true } });
    }

    // ── 403: deactivated account ────
    if (error.response?.status === 403 && error.response?.data?.is_deactivated === true) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('bearer');
      window.dispatchEvent(new Event("authLogout")); 
      window.location.href = '/deactivated-account';
      return Promise.resolve({ data: { success: false, is_deactivated: true } });
    }

    return Promise.reject(error);
  }
);

// Sync auth token changes across browser tabs to avoid stale refresh requests
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'accessToken' && event.newValue) {
      api.defaults.headers.common['Authorization'] = `Bearer ${event.newValue}`;
    }
  });
}

export default api;
